import _ from 'lodash';
import {
  parseAmount,
  friendlyDate,
  friendlyTime,
  friendlyDateTime,
  getTimeFromDateTime,
  simpleDateNoTime,
} from 'app/utils/parsers';
import IApplicationState from 'app/types/state';

interface IStatusBlock {
  paymentStatus?: string;
  paymentStatusDue?: string;
  transferStatus?: string;
  adminStatus?: string;
  forfeitStatus?: string;
  activeStatus?: string;
  fixtureMatchStatus?: string;
  refundStatus?: string;
}

const defaultStatusBlock: IStatusBlock = {
  paymentStatus: 'pending',
  paymentStatusDue: null,
  transferStatus: null,
  adminStatus: null,
  forfeitStatus: null,
  activeStatus: null,
  fixtureMatchStatus: null,
};

export function parsePaymentStatusText(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultText = 'Unpaid',
  extensivePaid = false,
) {
  let parsedText = defaultText;
  if (!extensivePaid && status?.paymentStatus === 'paid') {
    parsedText = 'Teammo';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-transferred') {
    parsedText = 'Teammo';
  }
  if (extensivePaid && status?.paymentStatus === 'paid') {
    parsedText = 'Paid Teammo';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-transferred') {
    parsedText = 'Paid via Transfer';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-cash') {
    parsedText = 'Cash';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-eftpos') {
    parsedText = 'Eftpos';
  }
  if (!extensivePaid && status?.paymentStatus === 'paid-prepaid') {
    parsedText = 'Pre-paid';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-cash') {
    parsedText = 'Paid Cash';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-eftpos') {
    parsedText = 'Paid Eftpos';
  }
  if (extensivePaid && status?.paymentStatus === 'paid-prepaid') {
    parsedText = 'Paid Pre-paid';
  }
  if (status?.paymentStatus === 'paid-refunded') {
    parsedText = 'Refunded';
  }
  if (status?.transferStatus === 'refund-pending') {
    parsedText = 'Processing Refund';
  }
  if (status?.transferStatus === 'refunded') {
    parsedText = 'Refunded';
  }
  if (status?.transferStatus === 'partially-refunded') {
    parsedText = 'Partially Refunded';
  }
  if (
    status?.paymentStatus === 'pending'
    && status?.paymentStatusDue === 'overdue'
  ) {
    parsedText = 'Overdue';
  }
  if (status?.adminStatus === 'no-show') {
    parsedText = 'No-show';
  }
  if (otherTeamStatus?.adminStatus === 'no-show') {
    parsedText = 'Opposition No-show';
  }
  if (
    status?.adminStatus === 'cancelled'
    || otherTeamStatus?.adminStatus === 'cancelled'
  ) {
    parsedText = 'Cancelled';
  }
  if (status?.fixtureMatchStatus === 'cancelled') {
    parsedText = 'Match Cancelled';
  }
  if (status?.forfeitStatus === 'walkover') {
    parsedText = 'Forfeit';
  }
  if (otherTeamStatus?.forfeitStatus === 'walkover') {
    parsedText = 'Opposition Forfeit';
  }
  if (status?.activeStatus === 'cancelled') {
    parsedText = 'Cancelled';
  }
  if (status?.transferStatus === 'transfer-required') {
    parsedText = 'Transfer Pending';
  }
  if (status?.transferStatus === 'transferred') {
    parsedText = 'Transferred';
  }
  if (status?.transferStatus === 'refunded') {
    parsedText = 'Refunded';
  }
  return parsedText;
}

function parsePaymentStatusDescription(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultText = null,
) {
  let parsedText = defaultText;
  if (status?.transferStatus === 'transfer-required') {
    parsedText = 'We are processing the transfer of this match payment. Once the next match is available it will be automatically carried over. If this is the last match of the season, we will provide a refund. This process takes 1-3 business days.';
  }
  if (status?.transferStatus === 'transferred') {
    parsedText = 'This match payment has been carried over to the next match.';
  }
  if (status?.transferStatus === 'refunded') {
    parsedText = 'This match payment has been refunded to the original payment method. Please allow 1-3 business days for it to show in your account.';
  }
  return parsedText;
}

function parsePaymentStatusColour(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  defaultColour = 'grey',
) {
  let parsedColour = defaultColour;
  if (status?.paymentStatus === 'paid') {
    parsedColour = 'purple';
  }
  if (status?.fixtureMatchStatus === 'cancelled') {
    parsedColour = 'black';
  }
  if (status?.paymentStatus === 'paid-cash') {
    parsedColour = 'orange';
  }
  if (status?.paymentStatus === 'paid-eftpos') {
    parsedColour = 'blue';
  }
  if (status?.paymentStatus === 'paid-prepaid') {
    parsedColour = 'yellow';
  }
  if (status?.paymentStatus === 'paid-transferred') {
    parsedColour = 'purple';
  }
  if (status?.transferStatus === 'refund-pending') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'refunded') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'partially-refunded') {
    parsedColour = 'dark-purple';
  }
  if (status?.paymentStatus === 'paid-refunded') {
    parsedColour = 'dark-purple';
  }
  if (status?.paymentStatus === 'pending') {
    parsedColour = 'grey';
  }
  if (status?.activeStatus === 'cancelled') {
    parsedColour = 'black';
  }
  if (
    status?.paymentStatus === 'pending'
    && status?.paymentStatusDue === 'overdue'
  ) {
    parsedColour = 'red';
  }
  if (
    status?.adminStatus === 'cancelled'
    || otherTeamStatus?.adminStatus === 'cancelled'
  ) {
    parsedColour = 'black';
  }
  if (
    status?.adminStatus === 'no-show'
    || otherTeamStatus?.adminStatus === 'no-show'
  ) {
    parsedColour = 'black';
  }
  if (status?.transferStatus === 'transfer-required') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'transferred') {
    parsedColour = 'dark-purple';
  }
  if (status?.transferStatus === 'refunded') {
    parsedColour = 'dark-purple';
  }
  if (
    status?.adminStatus === 'no-show'
    || otherTeamStatus?.forfeitStatus === 'walkover'
  ) {
    parsedColour = 'black';
  }
  if (
    status?.forfeitStatus === 'walkover'
    || otherTeamStatus?.forfeitStatus === 'walkover'
  ) {
    parsedColour = 'black';
  }
  return parsedColour;
}

export function parsePaymentStatus(
  status = defaultStatusBlock,
  otherTeamStatus = defaultStatusBlock,
  extensivePaid = false,
) {
  // console.log('status');
  // console.log(status);
  // console.log('otherTeamStatus');
  // console.log(otherTeamStatus);
  let statuses = [];
  const colour = parsePaymentStatusColour(
    {
      paymentStatus: status?.paymentStatus,
      paymentStatusDue: status?.paymentStatusDue,
    },
    {},
  );
  const text = parsePaymentStatusText(
    {
      paymentStatus: status?.paymentStatus,
      paymentStatusDue: status?.paymentStatusDue,
    },
    {},
    'Unpaid',
    extensivePaid,
  );
  const colour2 = parsePaymentStatusColour(
    {
      forfeitStatus: status?.forfeitStatus,
    },
    otherTeamStatus,
  );
  const text2 = parsePaymentStatusText(
    {
      forfeitStatus: status?.forfeitStatus,
    },
    otherTeamStatus,
    null,
    extensivePaid,
  );
  const colour3 = parsePaymentStatusColour(
    {
      adminStatus: status?.adminStatus,
      fixtureMatchStatus: status?.fixtureMatchStatus,
    },
    otherTeamStatus,
  );
  const text3 = parsePaymentStatusText(
    {
      adminStatus: status?.adminStatus,
      fixtureMatchStatus: status?.fixtureMatchStatus,
    },
    otherTeamStatus,
    null,
    extensivePaid,
  );
  const transferColour = parsePaymentStatusColour(
    {
      transferStatus: status?.transferStatus,
    },
    null,
    null,
  );
  const transferText = parsePaymentStatusText(status, null, null);
  const transferDescription = parsePaymentStatusDescription(
    {
      transferStatus: status?.transferStatus,
    },
    null,
    null,
  );

  if (
    !extensivePaid
    && (text === 'Teammo'
      || text === 'Cash'
      || text === 'Eftpos'
      || text === 'Pre-paid'
      || text === 'Transferred'
      || text === 'Refunded')
  ) {
    statuses.push({ colour: 'green', text: 'Paid' });
  }
  statuses.push({ colour, text });
  statuses.push({ colour: colour2, text: text2 });
  statuses.push({ colour: colour3, text: text3 });
  // console.log(text);
  // console.log(otherTeamStatus);
  // console.log(text2);
  // console.log(colour2);
  // console.log(text3);
  // console.log(statuses);
  if (transferColour && transferText) {
    statuses.push({
      colour: transferColour,
      text: transferText,
      description: transferDescription,
    });
  }

  statuses = _.filter(statuses, (s) => !_.isEmpty(s.text));
  statuses = _.uniqBy(statuses, 'text');

  if (
    statuses.length === 2
    && statuses[0]?.text === 'Unpaid'
    && (statuses[1]?.text === 'Forfeit'
      || statuses[1]?.text === 'Opposition Forfeit'
      || statuses[1]?.text === 'Match Cancelled'
      || statuses[1]?.text === 'Cancelled'
      || statuses[1]?.text === 'No-show'
      || statuses[1]?.text === 'Opposition No-show')
  ) {
    statuses = [statuses[1]];
  }
  statuses = _.uniqBy(statuses, 'text');
  return statuses;
}

function getPaymentEventForGroup(paymentEvent, groupId) {
  let groupData = {};
  if (_.isEmpty(paymentEvent.groupOne) || _.isEmpty(paymentEvent.groupTwo)) {
    return {};
  }

  const groupOneStatusBlock = {
    paymentStatus: paymentEvent.groupOneMatchPaymentStatus,
    transferStatus: paymentEvent.groupOneMatchPaymentTransferStatus,
    adminStatus: paymentEvent.groupOneMatchPaymentAdminStatus,
    forfeitStatus: paymentEvent.groupOneMatchPaymentForfeitStatus,
    activeStatus: paymentEvent.groupOneMatchPaymentActiveStatus,
  };

  const groupTwoStatusBlock = {
    paymentStatus: paymentEvent.groupTwoMatchPaymentStatus,
    transferStatus: paymentEvent.groupTwoMatchPaymentTransferStatus,
    adminStatus: paymentEvent.groupTwoMatchPaymentAdminStatus,
    forfeitStatus: paymentEvent.groupTwoMatchPaymentForfeitStatus,
    activeStatus: paymentEvent.groupTwoMatchPaymentActiveStatus,
  };

  if (paymentEvent.groupOne.id === groupId) {
    const matchPaymentId = paymentEvent.groupOneMatchPaymentForfeitStatus === 'walkover'
      ? paymentEvent.groupOneForfeit.id
      : paymentEvent.groupOneMatchPayment.id;

    groupData = {
      name: paymentEvent.groupOneName,
      amount: parseAmount(paymentEvent.groupOneMatchPaymentAmount),
      statuses: parsePaymentStatus(groupOneStatusBlock, groupTwoStatusBlock),
      status: paymentEvent.groupOneMatchPaymentStatus,
      otherTeamStatus: paymentEvent.groupTwoMatchPaymentStatus,
      matchPaymentId,
      paymentEventId: paymentEvent.id,
      metadata: paymentEvent.groupOnePaymentMetadata,
      paymentCategory: paymentEvent.groupOnePaymentCategory,
    };
  }
  if (paymentEvent.groupTwo.id === groupId) {
    const matchPaymentId = paymentEvent.groupTwoMatchPaymentForfeitStatus === 'walkover'
      ? paymentEvent.groupTwoForfeit.id
      : paymentEvent.groupTwoMatchPayment.id;

    groupData = {
      name: paymentEvent.groupTwoName,
      amount: parseAmount(paymentEvent.groupTwoMatchPaymentAmount),
      statuses: parsePaymentStatus(groupOneStatusBlock, groupTwoStatusBlock),
      status: paymentEvent.groupTwoMatchPaymentStatus,
      otherTeamStatus: paymentEvent.groupOneMatchPaymentStatus,
      matchPaymentId,
      paymentEventId: paymentEvent.id,
      metadata: paymentEvent.groupTwoPaymentMetadata,
      paymentCategory: paymentEvent.groupTwoPaymentCategory,
    };
  }
  return groupData;
}

export function getCurrentPaymentEvent(state) {
  if (
    state.paymentEvents.paymentEventsById !== undefined
    && state.paymentEvents.currentPaymentEventId !== undefined
  ) {
    const findPaymentEvent = state.paymentEvents.paymentEventsById[
      state.paymentEvents.currentPaymentEventId
    ];
    const currentPaymentEvent = {
      ...findPaymentEvent,
    };

    return currentPaymentEvent;
  }
  return {};
}

function getPaymentEventsByIdSortedDate(paymentEventsById): any {
  if (paymentEventsById !== undefined) {
    const sortedPaymentEventsById = {};
    const paymentEventsArray = _.map(
      paymentEventsById,
      (paymentEvent) => paymentEvent,
    );
    const sortedArray = paymentEventsArray.sort(
      (a, b) => new Date(b.eventAt) - new Date(a.eventAt),
    );
    _.each(sortedArray, (pe) => {
      sortedPaymentEventsById[pe.id] = pe;
    });
    return sortedPaymentEventsById;
  }
  return {};
}

// export function getPaymentEventsByIdSortedDateFilterByStatus(
//   paymentEventsById,
//   groupId,
//   status = 'all',
// ): any {
//   if (paymentEventsById !== undefined) {
//     const filteredPaymentEventsById = _.filter(paymentEventsById, (event) => {
//       const groupData = getPaymentEventForGroup(event, groupId);
//       switch (status) {
//         case 'pending':
//           return (
//             _.includes(['pending'], groupData.status)
//             && !_.includes(['walkover'], groupData.otherTeamStatus)
//           );
//         case 'paid':
//           return (
//             _.includes(
//               [
//                 'paid',
//                 'paid-cash',
//                 'paid-eftpos',
//                 'paid-transferred',
//                 'paid-refunded',
//                 'paid-prepaid',
//               ],
//               groupData.status,
//             ) && !_.includes(['walkover'], groupData.otherTeamStatus)
//           );
//         case 'refunded':
//           return (
//             _.includes(['refunded'], groupData.status)
//             && !_.includes(['walkover'], groupData.otherTeamStatus)
//           );
//         case 'forfeited':
//           return (
//             _.includes(['walkover'], groupData.status)
//             || _.includes(['walkover'], groupData.otherTeamStatus)
//           );
//         case 'all':
//           return true;
//         default:
//           return true;
//       }
//     });

//     return getPaymentEventsByIdSortedDate(filteredPaymentEventsById);
//   }
//   return {};
// }

export function getPaymentEvents(state) {
  return {
    ...state.paymentEvents,
    currentPaymentEvent: {
      ...getCurrentPaymentEvent(state),
    },
    paymentEventsByIdSortedDate: getPaymentEventsByIdSortedDate(
      state.paymentEvents.paymentEventsById,
    ),
  };
}

export function getPaymentEventsByIds(paymentEventsById, paymentEventIds) {
  if (paymentEventsById !== undefined && paymentEventIds != null) {
    return _.pick(paymentEventsById, paymentEventIds);
  }
  return {};
}

export function getPaymentEventsByObjectId(
  paymentEventsById,
  object,
  objectId,
) {
  if (paymentEventsById !== undefined && objectId != null) {
    // console.log(paymentEvents, objectId);
    const filteredPaymentEvents = _.filter(paymentEventsById, {
      [object]: { id: objectId },
    });

    const paymentEventIds = _.map(filteredPaymentEvents, 'id');
    const newPaymentEventsById = _.pick(paymentEventsById, paymentEventIds);

    return {
      paymentEventsById: newPaymentEventsById,
      paymentEventIds,
    };
  }
  return {};
}

// export function getPaymentEventForMatchCard(
//   paymentEvent,
//   groupId,
// ): IPaymentCardData {
//   const groupData = getPaymentEventForGroup(paymentEvent, groupId);

//   if (_.isEmpty(groupData)) {
//     return undefined;
//   }
//   return {
//     paymentEventId: groupData.paymentEventId,
//     matchPaymentId: groupData.matchPaymentId,
//     round: paymentEvent.description,
//     dateTime: friendlyDate(paymentEvent.eventAt),
//     eventInfo: paymentEvent.eventInfo,
//     amount: groupData.amount,
//     section: paymentEvent.sectionTag,
//     time: friendlyTime(paymentEvent.eventAt),
//     location: paymentEvent.locationInfo,
//     statuses: groupData.statuses,
//     status: groupData.status,
//     otherTeamStatus: groupData.otherTeamStatus,
//     paymentCategory: groupData.paymentCategory,
//     metadata: groupData.metadata,
//   };
// }
export function getGroupName(paymentEvent, groupId): string {
  const groupData = getPaymentEventForGroup(paymentEvent, groupId);

  if (_.isEmpty(groupData)) {
    return undefined;
  }
  return groupData.name;
}

export function getPaymentEventForDashboard(paymentEvent) {
  if (!_.isEmpty(paymentEvent)) {
    const summaryTime = friendlyDateTime(paymentEvent.eventAt);
    const summaryTimeSimple = friendlyTime(paymentEvent.eventAt);
    let status = 'Unpaid';

    if (paymentEvent.status !== 'active') {
      status = paymentEvent.status;
    }
    if (
      (paymentEvent.groupOneMatchPaymentStatus === 'paid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-eftpos'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-transferred')
      && (paymentEvent.groupTwoMatchPaymentStatus === 'paid'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-eftpos'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-transferred')
    ) {
      status = 'Paid';
    }

    if (
      ((paymentEvent.groupOneMatchPaymentStatus === 'paid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-eftpos'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-transferred')
        && !(
          paymentEvent.groupTwoMatchPaymentStatus === 'paid'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-cash'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-prepaid'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-eftpos'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-transferred'
        ))
      || (!(
        paymentEvent.groupOneMatchPaymentStatus === 'paid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-eftpos'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-transferred'
      )
        && (paymentEvent.groupTwoMatchPaymentStatus === 'paid'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-cash'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-prepaid'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-eftpos'
          || paymentEvent.groupTwoMatchPaymentStatus === 'paid-transferred'))
    ) {
      status = 'Partially Paid';
    }

    if (
      paymentEvent.status === 'cancelled'
      || paymentEvent.groupOneMatchPaymentActiveStatus === 'cancelled'
      || paymentEvent.groupTwoMatchPaymentActiveStatus === 'cancelled'
    ) {
      status = 'Cancelled';
    }

    if (
      paymentEvent.groupOneMatchPaymentAdminStatus === 'no-show'
      || paymentEvent.groupTwoMatchPaymentAdminStatus === 'no-show'
    ) {
      status = 'No-show';
    }

    if (
      paymentEvent.groupOneMatchPaymentForfeitStatus === 'walkover'
      || paymentEvent.groupTwoMatchPaymentForfeitStatus === 'walkover'
    ) {
      status = 'Forfeit';
    }
    const groupOneStatusBlock = {
      paymentStatus: paymentEvent.groupOneMatchPaymentStatus,
      transferStatus: paymentEvent.groupOneMatchPaymentTransferStatus,
      adminStatus: paymentEvent.groupOneMatchPaymentAdminStatus,
      forfeitStatus: paymentEvent.groupOneMatchPaymentForfeitStatus,
      activeStatus: paymentEvent.groupOneMatchPaymentActiveStatus,
    };

    const groupTwoStatusBlock = {
      paymentStatus: paymentEvent.groupTwoMatchPaymentStatus,
      transferStatus: paymentEvent.groupTwoMatchPaymentTransferStatus,
      adminStatus: paymentEvent.groupTwoMatchPaymentAdminStatus,
      forfeitStatus: paymentEvent.groupTwoMatchPaymentForfeitStatus,
      activeStatus: paymentEvent.groupTwoMatchPaymentActiveStatus,
    };

    const groupOnePaymentText = parsePaymentStatusText({
      paymentStatus: groupOneStatusBlock.paymentStatus,
      adminStatus: groupOneStatusBlock.adminStatus,
      forfeitStatus: groupOneStatusBlock.forfeitStatus,
    });
    const groupTwoPaymentText = parsePaymentStatusText({
      paymentStatus: groupTwoStatusBlock.paymentStatus,
      adminStatus: groupTwoStatusBlock.adminStatus,
      forfeitStatus: groupTwoStatusBlock.forfeitStatus,
    });

    let groupOneMatchPaymentId;
    let groupTwoMatchPaymentId;
    if (
      paymentEvent
      && paymentEvent.groupOneMatchPayment !== undefined
      && paymentEvent.groupTwoMatchPayment !== undefined
      && paymentEvent.groupOneMatchPayment !== null
      && paymentEvent.groupTwoMatchPayment !== null
    ) {
      groupOneMatchPaymentId = paymentEvent.groupOneMatchPayment.id;
      groupTwoMatchPaymentId = paymentEvent.groupTwoMatchPayment.id;
    }
    let groupOneSubscriptionPaymentId;
    let groupTwoSubscriptionPaymentId;
    if (
      paymentEvent
      && paymentEvent.groupOneSubscriptionPayment !== undefined
      && paymentEvent.groupTwoSubscriptionPayment !== undefined
      && paymentEvent.groupOneSubscriptionPayment !== null
      && paymentEvent.groupTwoSubscriptionPayment !== null
    ) {
      groupOneSubscriptionPaymentId = paymentEvent.groupOneSubscriptionPayment.id;
      groupTwoSubscriptionPaymentId = paymentEvent.groupTwoSubscriptionPayment.id;
    }
    const eventObject = {
      status,
      groupOneMatchPaymentId,
      groupTwoMatchPaymentId,
      groupOneSubscriptionPaymentId,
      groupTwoSubscriptionPaymentId,
      groupOneName: paymentEvent.groupOneName,
      groupTwoName: paymentEvent.groupTwoName,
      groupOnePaymentData: {
        metadata: paymentEvent.groupOneMatchPaymentMetadata,
        paymentType: paymentEvent.groupOneMatchPaymentType,
        amount: parseAmount(paymentEvent.groupOneMatchPaymentAmount),
        forfeitAmount: parseAmount(
          paymentEvent.groupOneMatchPaymentForfeitAmount,
        ),
        receiptReference: paymentEvent.groupOneMatchPaymentReceiptReference,
        datePaid: simpleDateNoTime(paymentEvent.groupOneMatchPaymentPaidAt),
      },
      groupTwoPaymentData: {
        metadata: paymentEvent.groupTwoMatchPaymentMetadata,
        paymentType: paymentEvent.groupTwoMatchPaymentType,
        amount: parseAmount(paymentEvent.groupTwoMatchPaymentAmount),
        forfeitAmount: parseAmount(
          paymentEvent.groupTwoMatchPaymentForfeitAmount,
        ),
        receiptReference: paymentEvent.groupTwoMatchPaymentReceiptReference,
        datePaid: simpleDateNoTime(paymentEvent.groupTwoMatchPaymentPaidAt),
      },
      groupOneChecked:
        paymentEvent.groupOneMatchPaymentStatus === 'paid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupOneMatchPaymentStatus === 'paid-eftpos',
      groupOnePaymentText,
      groupTwoChecked:
        paymentEvent.groupTwoMatchPaymentStatus === 'paid'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-cash'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-prepaid'
        || paymentEvent.groupTwoMatchPaymentStatus === 'paid-eftpos',
      groupTwoPaymentText,
      eventSection: paymentEvent.sectionTag,
      eventLocation: paymentEvent.locationInfo,
      groupOnePaymentUrl: `${TEAMMO_PAY_APP_URL}/${paymentEvent.groupOneMatchPaymentReference}`,
      groupTwoPaymentUrl: `${TEAMMO_PAY_APP_URL}/${paymentEvent.groupTwoMatchPaymentReference}`,
      eventTime: summaryTime,
      eventTimeSimple: summaryTimeSimple,
      reference: paymentEvent.reference,
      fixtureMatchStatus: paymentEvent.status,
      id: paymentEvent.id,
    };

    return eventObject;
  }
  return {};
}

export function getPaymentEventsForDashboard(paymentEvents, paymentEventIds) {
  if (paymentEvents === undefined) {
    return [];
  }

  const paymentEventsByTime = {};

  _.map(paymentEventIds, (paymentEventId) => {
    const paymentEvent = paymentEvents[paymentEventId];
    if (paymentEvent !== undefined) {
      const time = getTimeFromDateTime(paymentEvent.eventAt);
      const eventObject = getPaymentEventForDashboard(paymentEvent);
      if (time !== 'Invalid date') {
        if (paymentEventsByTime[time] === undefined) {
          paymentEventsByTime[time] = [];
        }
        paymentEventsByTime[time].push(eventObject);
      }
    }
  });
  const timeKeys = _.keys(paymentEventsByTime);
  const sortedTimeKeys = timeKeys.sort(
    (a, b) => new Date(`1970/01/01 ${_.replace(a, '.', ':')}`)
      - new Date(`1970/01/01 ${_.replace(b, '.', ':')}`),
  );

  const sortedPaymentEventsByTime = {};
  _.map(sortedTimeKeys, (time) => {
    sortedPaymentEventsByTime[time] = _.sortBy(
      paymentEventsByTime[time],
      'locationInfo',
    );
  });

  return sortedPaymentEventsByTime;
}
function setSummaryCount(paymentData, status, amount) {
  if (amount !== null && status !== null) {
    let countPayments = 0;
    if (
      paymentData[status] !== undefined
      && paymentData[status][amount.toString()] !== undefined
    ) {
      countPayments = paymentData[status][amount.toString()] + 1;
    } else {
      countPayments = 1;
    }
    return _.set(paymentData, status, {
      ...paymentData[status],
      [amount.toString()]: countPayments,
    });
  }

  return paymentData;
}

export function getPaymentEventReport(paymentEventsById, paymentEventIds) {
  let paymentData = {};
  let event;

  _.map(paymentEventIds, (eventId) => {
    event = paymentEventsById[eventId];
    paymentData = setSummaryCount(
      paymentData,
      event.groupOneMatchPaymentStatus,
      event.groupOneMatchPaymentAmount,
    );
    paymentData = setSummaryCount(
      paymentData,
      event.groupTwoMatchPaymentStatus,
      event.groupTwoMatchPaymentAmount,
    );
  });

  const paymentDataArr = [];
  _.map(paymentData, (statusValue, statusKey) => {
    _.map(statusValue, (amountValue, amountKey) => {
      paymentDataArr.push({
        paymentMethod: parsePaymentStatusText({
          paymentStatus: statusKey,
        }),
        numberOfPayments: amountValue,
        paymentRate: parseAmount(parseInt(amountKey, 10)),
        paymentTotal: parseAmount(parseInt(amountKey, 10) * amountValue),
      });
    });
  });
  return paymentDataArr;
}

export function getPaymentEventsByIdInSelectFormat(paymentEvents): any {
  const locations = _.uniq(
    _.remove(_.map(paymentEvents, (n) => n.locationInfo)),
    (e) => e === undefined,
  );

  return _.map(locations, (l) => {
    if (l !== undefined) {
      return { id: l, name: l };
    }
  });
}
